import { z } from 'zod';

export const CompleteInstallationRequest = z.object({
  code: z.string(),
  www: z.boolean().optional(),
});
export type CompleteInstallationRequest = z.infer<typeof CompleteInstallationRequest>;

export const CompleteInstallationResponse = z.object({
  ok: z.literal(true),
});
export type CompleteInstallationResponse = z.infer<typeof CompleteInstallationResponse>;

export const DisconnectResponse = z.object({
  ok: z.literal(true),
});
export type DisconnectResponse = z.infer<typeof DisconnectResponse>;

export const SlackIntegrationStatus = z.enum(['installed', 'not_installed']);
export type SlackIntegrationStatus = z.infer<typeof SlackIntegrationStatus>;

export const SlackIntegrationStatusResponse = z.object({
  status: SlackIntegrationStatus,
});
export type SlackIntegrationStatusResponse = z.infer<
  typeof SlackIntegrationStatusResponse
>;

export const SlackChannel = z.object({
  id: z.string(),
  name: z.string(),
  isMember: z.boolean(),
});
export type SlackChannel = z.infer<typeof SlackChannel>;

export const ChannelsResponse = z.object({
  channels: z.array(SlackChannel),
});
export type ChannelsResponse = z.infer<typeof ChannelsResponse>;

export const ChannelResponse = z.object({
  channel: SlackChannel,
});
export type ChannelResponse = z.infer<typeof ChannelResponse>;

// Slack Events API DTOs
export const SlackEventWrapper = z.object({
  token: z.string(),
  team_id: z.string(),
  api_app_id: z.string(),
  event: z.record(z.any()), // Generic event object - can be refined later for specific event types
  type: z.literal('event_callback'),
  event_id: z.string(),
  event_time: z.number(),
  authorizations: z
    .array(
      z.object({
        enterprise_id: z.string().optional().nullable(),
        team_id: z.string(),
        user_id: z.string(),
        is_bot: z.boolean(),
        is_enterprise_install: z.boolean().optional(),
      }),
    )
    .optional(),
});
export type SlackEventWrapper = z.infer<typeof SlackEventWrapper>;

export const SlackUrlVerification = z.object({
  token: z.string(),
  challenge: z.string(),
  type: z.literal('url_verification'),
});
export type SlackUrlVerification = z.infer<typeof SlackUrlVerification>;

export const SlackEventRequest = z.union([SlackEventWrapper, SlackUrlVerification]);
export type SlackEventRequest = z.infer<typeof SlackEventRequest>;

export const SlackEventResponse = z.object({
  challenge: z.string().optional(),
});
export type SlackEventResponse = z.infer<typeof SlackEventResponse>;



/**
 * A curated list of Slack Web API error codes that typically indicate the workspace
 * integration (OAuth token / permissions) is mis-configured or has been revoked.
 *
 * Source: https://api.slack.com/methods/chat.postMessage#errors and other Web API docs.
 */
export const SLACK_INTEGRATION_ERROR_CODES = [
  // Authentication / permission issues
  'invalid_auth',
  'not_authed',
  'account_inactive',
  'token_revoked',
  'missing_scope',
  'invalid_permissions',
  // Token / app configuration problems
  'app_not_installed',
  'org_login_required',
  'org_token_revoked',
] as const;

/**
 * Union type representing any error that suggests the Slack integration needs
 * to be re-installed or re-authorized.
 */
export type SlackIntegrationErrorCode =
  typeof SLACK_INTEGRATION_ERROR_CODES[number];
